import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { VisibilityPredicateType } from "../../../registry/visibility";
import { IntegrationKey } from "@erp/shared-types";

/**
 * Send Famous Invoice to Orderful Step
 *
 * Fetches a Famous order by order number and sends it to Orderful as an EDI
 * Invoice (810 or 880 based on customer configuration).
 *
 * This step:
 * - Takes a Famous order number as input
 * - Fetches the order details from Famous Oracle database
 * - Validates the order data and customer information
 * - Builds and sends an 810 or 880 EDI message to Orderful
 * - Records the EDI transmission status
 *
 * Note: This is a backend-only step that requires Famous database access and
 * Orderful API integration. The step type remains "send_famous_invoice_to_orderful_810"
 * for DB compatibility with existing workflow instances.
 */
export const sendFamousInvoiceToOrderfulStep = defineStepConfig({
  type: "send_famous_invoice_to_orderful_810",
  name: "Send Famous Invoice to Orderful",
  description:
    "Fetch a Famous order by order number and send it to Orderful as an EDI Invoice (810 or 880 based on customer configuration)",
  category: "Integration",
  libraryModule: "Famous",
  visibility: [
    [
      {
        type: VisibilityPredicateType.Integration,
        integration: IntegrationKey.Famous,
      },
    ],
  ],
  inputSchema: z.object({
    famousOrderNumber: z
      .string()
      .min(1)
      .describe("The Famous order number to process"),
  }),

  outputSchema: z.object({
    orderfulTransactionId: z
      .string()
      .describe("The Orderful transaction ID for the Invoice"),
    famousSalesOrderId: z
      .string()
      .uuid()
      .describe("The internal database ID of the Famous sales order record"),
    purchaseOrderNumber: z
      .string()
      .optional()
      .describe("The customer's purchase order number from the order"),
  }),

  configSchema: z.object({
    stream: z
      .enum(["TEST", "LIVE"])
      .default("TEST")
      .describe("Orderful stream to send to (TEST or LIVE)"),
    skipIfAlreadySent: z
      .boolean()
      .default(true)
      .describe(
        "Skip sending if the invoice has already been sent for this order",
      ),
    fetchFromFamous: z
      .boolean()
      .default(true)
      .describe(
        "Whether to fetch fresh data from Famous (if false, uses existing cached data)",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "5 minutes",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "10 seconds",
      maximumInterval: "2 minutes",
    },
  },
});
